import React from 'react';
import { Box, Button, Typography, Card } from '@ellucian/react-design-system/core';
import { CircularProgress } from '@ellucian/react-design-system/core';
import './styles/CoreIntegrationWidget.css';

interface CoreIntegrationWidgetProps {
  bannerCount?: number;
  bannerMax?: number;
  colleagueCount?: number;
  colleagueMax?: number;
  erpTypes?: string[];
  preferredErp?: string;  // Which ERP is preferred (determines which counter to show)
  onViewOptions?: () => void;
  isLoading?: boolean;
}

/**
 * CoreIntegrationWidget
 * Displays core integration selection status with circular progress
 * Shows separate quotas for Banner and Colleague ERPs
 */
const CoreIntegrationWidget: React.FC<CoreIntegrationWidgetProps> = ({
  bannerCount = 0,
  bannerMax = 30,
  colleagueCount = 0,
  colleagueMax = 30,
  erpTypes = [],
  preferredErp,
  onViewOptions,
  isLoading = false,
}) => {
  
  // Use preferredErp if provided, otherwise infer from erpTypes
  const displayErp = preferredErp || 
                     (erpTypes.includes('Banner') ? 'Banner' : 
                      erpTypes.includes('Colleague') ? 'Colleague' : 
                      'Banner');
  
  const displayCount = displayErp === 'Banner' ? bannerCount : colleagueCount;
  const displayMax = displayErp === 'Banner' ? bannerMax : colleagueMax;
  const progressPercentage = (displayCount / displayMax) * 100;

  return (
    <Card
      className="core-integration-widget"
      sx={{
        backgroundColor: '#ffffff',
        borderRadius: '6px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
      }}
    >
      {/* Header */}
      <Typography
        className="core-integration-header"
        sx={{
          fontWeight: 700,
          color: '#1a1a1a',
          margin: 0,
          width: '100%',
        }}
      >
        Core Integration Selection
      </Typography>

      {/* Main Content: Circular Progress + Text */}
      <Box
        className="core-integration-content"
        sx={{
          display: 'flex',
          width: '100%',
        }}
      >
        {/* Left: Circular Progress */}
        <Box
          className="core-integration-circle"
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {/* Background Circle */}
          <CircularProgress
            variant="determinate"
            value={100}
            size={90}
            thickness={2}
            aria-hidden="true"
            sx={{
              color: '#e0e0e0',
              position: 'absolute',
            }}
          />
          
          {/* Foreground Progress Circle */}
          <CircularProgress
            variant="determinate"
            value={Math.max(5, progressPercentage)}
            size={90}
            thickness={2}
            aria-label={`Integration progress: ${Math.round(progressPercentage)}%`}
            sx={{
              color: '#0066CC',
              position: 'absolute',
              transform: 'rotate(-90deg)'
            }}
          />
          
          {/* Center Text */}
          <Box
            sx={{
              position: 'absolute',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '2px',
            }}
          >
            <Typography
              className="core-integration-circle-number"
              sx={{
                fontWeight: 700,
                color: '#1a1a1a',
              }}
            >
              {displayCount}/{displayMax}
            </Typography>
          </Box>
        </Box>

        {/* Right: Description + ERP Label */}
        <Box
          className="core-integration-text-box"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            gap: '4px',
            flex: 1,
          }}
        >
          <Typography
            className="core-integration-title"
            sx={{
              color: '#1a1a1a',
              fontFamily: 'Proxima Nova, sans-serif',
              lineHeight: '1.5',
              margin: 0,
              fontWeight: 700,
            }}
          >
            Your subscription includes up to {displayMax} Ellucian-delivered core integrations.
          </Typography>
          
          <Typography
            className="core-integration-description"
            sx={{
              color: '#666666',
              fontFamily: 'Proxima Nova, sans-serif',
              lineHeight: '1.5',
              margin: 0,
            }}
          >
            Select the ones you want to be able to<br />install from their individual listings.
          </Typography>
        </Box>
      </Box>

      {/* View Options Button */}
      <Button
        className="core-integration-button"
        onClick={onViewOptions}
        disabled={isLoading}
        style={{
          width: '100%',
          fontFamily: 'Proxima Nova, sans-serif',
          backgroundColor: '#7100EB',
          color: '#ffffff',
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: '6px',
          border: 'none',
          cursor: isLoading ? 'not-allowed' : 'pointer',
        }}
      >
        {isLoading ? 'Loading...' : 'View Core Integration Options'}
      </Button>
    </Card>
  );
};

export default CoreIntegrationWidget;
